import { median } from './hoehe';
import { utmZoneFuer, utmZuWgs84, wgs84ZuUtm, type Utm } from './utm';

/**
 * GPS-Mittelung (docs/maengel-verortung-plan.md 9.2/9.3, Paket P2).
 *
 * Wer steht, bekommt über einige Sekunden mehrere Fixe. Gemittelt wird in UTM
 * (Meter, eben), gewichtet mit 1/Genauigkeit². Die Genauigkeit des Mittels
 * wird bewusst NICHT mit √n kleiner gerechnet: aufeinanderfolgende Fixe
 * teilen Mehrwege- und Atmosphärenfehler — 30 Fixe sind nicht 30 unabhängige
 * Messungen. Höchstens Faktor 2 (√4) gegenüber dem einzelnen Fix.
 *
 * Reine Funktion, nichts wirft.
 */

/** Ein GPS-Fix: Grad WGS84, horizontale Genauigkeit (m, 68 %), Zeitpunkt Unix-ms. */
export interface GpsFix {
    lat: number;
    lng: number;
    genauigkeitM: number;
    t: number;
}

/** Mittel mehrerer Fixe samt UTM in der verwendeten Zone. */
export interface GpsMittel {
    lat: number;
    lng: number;
    genauigkeitM: number;
    streuungM: number;
    anzahl: number;
    dauerS: number;
    utm: Utm;
}

/** Schlechter gemeldete Fixe taugen für keinen Pin (Plan 9.3: Pin erst ab ≤ 8 m). */
const MAX_GENAUIGKEIT_M = 30;
/** Fixe, die mehr als so viel schlechter sind als der Median, fallen weg. */
const MEDIAN_FAKTOR = 3;
/** Höchstens so viele Fixe zählen als unabhängig (Faktor √4 = 2). */
const MAX_UNABHAENGIG = 4;
/** Untergrenze der Genauigkeit für die Gewichte (0 m = Division durch 0). */
const MIN_GENAUIGKEIT_M = 0.5;

/**
 * Fixe mit genauigkeitM > maxGenauigkeitM (Standard 30) oder > 3 × Median
 * (Median der danach verbliebenen) verwerfen; gewichtetes Mittel (1/acc²) in
 * UTM; streuungM = gewichteter RMS-Abstand zum Mittel; genauigkeitM =
 * max(streuungM, Median-Genauigkeit / √min(anzahl, 4)). `zone` erzwingt die
 * UTM-Zone, sonst die Standardzone des ersten verwertbaren Fixes. `null` ohne
 * verwertbare Fixe.
 */
export function gpsMitteln(fixe: readonly GpsFix[], opts?: { maxGenauigkeitM?: number; zone?: number }): GpsMittel | null {
    const grenze =
        typeof opts?.maxGenauigkeitM === 'number' && Number.isFinite(opts.maxGenauigkeitM) && opts.maxGenauigkeitM > 0
            ? opts.maxGenauigkeitM
            : MAX_GENAUIGKEIT_M;

    const brauchbar = (fixe ?? []).filter(
        (f) =>
            !!f &&
            Number.isFinite(f.lat) &&
            Number.isFinite(f.lng) &&
            Math.abs(f.lat) <= 90 &&
            Number.isFinite(f.genauigkeitM) &&
            f.genauigkeitM >= 0 &&
            f.genauigkeitM <= grenze,
    );
    if (brauchbar.length === 0) return null;

    const medianVorher = median(brauchbar.map((f) => f.genauigkeitM));
    const behalten = brauchbar.filter((f) => f.genauigkeitM <= MEDIAN_FAKTOR * Math.max(medianVorher, MIN_GENAUIGKEIT_M));

    const zone =
        typeof opts?.zone === 'number' && Number.isInteger(opts.zone) && opts.zone >= 1 && opts.zone <= 60
            ? opts.zone
            : utmZoneFuer(behalten[0].lng);

    let summeW = 0;
    let summeO = 0;
    let summeN = 0;
    const punkte = behalten.map((f) => {
        const u = wgs84ZuUtm(f.lat, f.lng, zone);
        const acc = Math.max(f.genauigkeitM, MIN_GENAUIGKEIT_M);
        const w = 1 / (acc * acc);
        summeW += w;
        summeO += w * u.ost;
        summeN += w * u.nord;
        return { ost: u.ost, nord: u.nord, w, t: f.t };
    });
    const ost = summeO / summeW;
    const nord = summeN / summeW;

    const streuungM = Math.sqrt(
        punkte.reduce((s, p) => s + p.w * ((p.ost - ost) ** 2 + (p.nord - nord) ** 2), 0) / summeW,
    );
    const medianGenauigkeit = median(behalten.map((f) => f.genauigkeitM));
    const genauigkeitM = Math.max(streuungM, medianGenauigkeit / Math.sqrt(Math.min(behalten.length, MAX_UNABHAENGIG)));

    const zeiten = punkte.map((p) => p.t).filter((t) => Number.isFinite(t));
    const dauerS = zeiten.length > 1 ? (Math.max(...zeiten) - Math.min(...zeiten)) / 1000 : 0;

    const utm: Utm = { zone, ost, nord };
    const { lat, lng } = utmZuWgs84(utm);
    return { lat, lng, genauigkeitM, streuungM, anzahl: behalten.length, dauerS, utm };
}
